import React, { useState } from "react";
import { Box, Typography, Button, CircularProgress, Chip, TextField, Divider, useMediaQuery } from "@material-ui/core";
import { useTheme } from "@material-ui/core/styles";
import FileCopyOutlinedIcon from "@material-ui/icons/FileCopyOutlined";
import AddRoundedIcon from "@material-ui/icons/AddRounded";
import { postAPIHandler } from "src/Apiconfig/service";
import Pagination from "./Pagination";

const truncateAddress = (address) => {
  if (!address || address.length <= 18) return address;
  return `${address.slice(0, 10)}…${address.slice(-8)}`;
};

// The page is now wide enough on anything but a phone (see index.js's maxWidth: 1100 - it was
// 720 before) that there's no real reason to hide the rest of the address behind a tooltip
// anymore. Truncate only where the screen genuinely doesn't have room for it.
const useAddressDisplay = () => {
  const theme = useTheme();
  const hasRoom = useMediaQuery(theme.breakpoints.up("sm"));
  return (address) => (hasRoom ? address : truncateAddress(address));
};

const formatDateRange = (first, last) => {
  try {
    const fmt = (d) => new Intl.DateTimeFormat("en-GB", { day: "2-digit", month: "short", year: "numeric" }).format(new Date(d));
    return fmt(first) === fmt(last) ? fmt(first) : `${fmt(first)} – ${fmt(last)}`;
  } catch (e) {
    return "";
  }
};

const AddressRow = ({ group, onMine, onNotMine, onReview, isLast, inkColor }) => {
  const theme = useTheme();
  const displayAddress = useAddressDisplay();
  const [busy, setBusy] = useState(null); // "mine" | "notmine" | null
  const [copied, setCopied] = useState(false);

  const run = async (kind, fn) => {
    setBusy(kind);
    try {
      await fn();
    } finally {
      setBusy(null);
    }
  };

  const copyAddress = (e) => {
    e.stopPropagation();
    navigator.clipboard?.writeText(group.address);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <Box sx={{ padding: "14px 4px", borderBottom: isLast ? "none" : `1px solid ${theme.palette.background.border}` }}>
      <Box display="flex" alignItems="center" justifyContent="space-between" style={{ gap: "16px", flexWrap: "wrap" }}>
        <Box sx={{ minWidth: 0, flex: "1 1 260px" }}>
          <Box display="flex" alignItems="center">
            <Typography variant="body2" color="primary" style={{ fontWeight: 700, fontFamily: "monospace" }} title={group.address}>
              {displayAddress(group.address)}
            </Typography>
            <Box onClick={copyAddress} style={{ cursor: "pointer", display: "flex", alignItems: "center", color: theme.palette.secondary.main, marginLeft: 8 }}>
              <FileCopyOutlinedIcon style={{ fontSize: 15 }} />
            </Box>
            {copied && (
              <Typography variant="caption" style={{ color: theme.palette.semantic.transferIn, fontWeight: 600, marginLeft: 6 }}>
                Copied
              </Typography>
            )}
          </Box>
          <Typography variant="body2" color="textSecondary" style={{ marginTop: 4, fontWeight: 500 }}>
            {group.transactionCount} txn{group.transactionCount === 1 ? "" : "s"} &middot; {group.sampleSource}
            {group.sampleWalletName ? ` · ${group.sampleWalletName}` : ""}
          </Typography>
          <Typography variant="caption" color="textSecondary" style={{ display: "block", marginTop: 2 }}>
            {formatDateRange(group.firstSeen, group.lastSeen)}
          </Typography>
          <Box display="flex" style={{ gap: "6px", flexWrap: "wrap", marginTop: "8px" }}>
            {group.coins?.slice(0, 5).map((coin) => (
              <Chip
                key={coin}
                label={coin}
                size="small"
                style={{ height: 20, fontSize: 10, fontWeight: 700, backgroundColor: theme.palette.background.border, color: inkColor }}
              />
            ))}
          </Box>
        </Box>

        <Box display="flex" alignItems="center" style={{ flexShrink: 0 }}>
          <Button
            size="small"
            variant="outlined"
            onClick={() => onReview(group)}
            disabled={!!busy}
            style={{ color: theme.palette.secondary.main, borderColor: theme.palette.background.border, textTransform: "none", marginRight: 12 }}
          >
            Review
          </Button>
          <Button
            size="small"
            variant="outlined"
            disabled={!!busy}
            onClick={() => run("notmine", () => onNotMine(group))}
            style={{ color: theme.palette.semantic.negative, borderColor: theme.palette.semantic.negative, textTransform: "none", marginRight: 12, minWidth: 92 }}
          >
            {busy === "notmine" ? <CircularProgress size={16} color="inherit" /> : "Not mine"}
          </Button>
          <Button
            size="small"
            variant="contained"
            color="primary"
            disabled={!!busy}
            onClick={() => run("mine", () => onMine(group))}
            style={{ textTransform: "none", minWidth: 92 }}
          >
            {busy === "mine" ? <CircularProgress size={16} color="inherit" /> : "Mine"}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

/**
 * "Which wallets are yours?" - a manual "mark an address as mine" input up top (now that
 * POST /v1/transfer-review/known-addresses is actually deployed - see index.js's onAddressAdded),
 * followed by addresses detected from the user's own transaction backlog (real backend
 * aggregation, GET /v1/transfer-review/address-groups), paginated the same way the Transactions
 * page paginates (see ./Pagination.js).
 */
const AddressGroupsPanel = ({ groups, loading, pageNumber, totalPages, onPageChange, onMine, onNotMine, onReview, onAddressAdded }) => {
  const theme = useTheme();
  const [inputValue, setInputValue] = useState("");
  const [adding, setAdding] = useState(false);
  const [addResult, setAddResult] = useState(null);

  // This app's light-theme text.primary token is a near-white color meant for dark mode (a
  // pre-existing theme bug, not fixed globally here). primary.main is correctly dark/light per
  // mode instead - it's what every "color=primary" Typography on this page already uses.
  const inkColor = theme.palette.primary.main;

  const hasGroups = groups && groups.length > 0;

  const handleAdd = async () => {
    const addresses = inputValue
      .split(/[\s,]+/)
      .map((a) => a.trim())
      .filter(Boolean);
    if (addresses.length === 0) return;
    setAdding(true);
    setAddResult(null);
    try {
      const res = await postAPIHandler({ endPoint: "transferReviewKnownAddresses", data: { addresses } });
      if (res?.status === 200) {
        setInputValue("");
        setAddResult({ ok: true });
        onAddressAdded?.();
      } else {
        setAddResult({ ok: false });
      }
    } catch (e) {
      setAddResult({ ok: false });
    } finally {
      setAdding(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleAdd();
    }
  };

  return (
    <Box
      sx={{
        borderRadius: "14px",
        border: `1px solid ${theme.palette.background.border}`,
        backgroundColor: theme.palette.background.bg4box,
        padding: "18px",
        marginBottom: "24px",
      }}
    >
      <Typography variant="body1" color="primary" style={{ fontWeight: 700 }}>
        Which wallets are yours?
      </Typography>
      <Typography variant="body2" color="textSecondary" style={{ marginTop: 2, marginBottom: 14 }}>
        Confirming one clears every matching transaction below at once.
      </Typography>

      <Typography variant="body2" color="primary" style={{ fontWeight: 600, marginBottom: 6 }}>
        Know a wallet address that's yours?
      </Typography>
      <Typography variant="caption" color="textSecondary" style={{ display: "block", marginBottom: 8 }}>
        Add a comma after each address to add another one.
      </Typography>
      <TextField
        fullWidth
        multiline
        minRows={3}
        maxRows={3}
        variant="outlined"
        placeholder={"0x388b2d29E7919D45b1B8F6e88b8A13a392f97b40,\n0x24aa201a55a03c76bf856ab6a21ebfe05eeb28bf,"}
        value={inputValue}
        onChange={(e) => setInputValue(e.target.value)}
        onKeyDown={handleKeyDown}
        sx={{
          backgroundColor: theme.palette.background.paper || "#fff",
          // MUI puts the real padding/typography for a multiline field's visible text on the
          // <textarea> itself, which carries the "MuiOutlinedInput-input" class - targeting
          // "MuiOutlinedInput-root" (the outer wrapper) or a bare "& textarea" selector both lose
          // to MUI's own more specific built-in rules, which is why padding/weight changes there
          // silently did nothing.
          "& .MuiOutlinedInput-input": {
            fontFamily: "monospace",
            fontWeight: 400,
            fontSize: 13,
            lineHeight: 1.9,
            color: inkColor,
            padding: "14px 16px",
          },
        }}
      />
      <Box display="flex" alignItems="center" justifyContent="flex-end" style={{ marginTop: "16px" }}>
        <Button
          variant="contained"
          color="primary"
          size="small"
          startIcon={<AddRoundedIcon />}
          disabled={!inputValue.trim() || adding}
          onClick={handleAdd}
          style={{ textTransform: "none", padding: "8px 18px" }}
        >
          {adding ? <CircularProgress size={18} color="inherit" /> : "Mark as mine"}
        </Button>
      </Box>
      {addResult?.ok && (
        <Typography variant="caption" style={{ display: "block", marginTop: 6, color: theme.palette.semantic.transferIn }}>
          Saved - checking your backlog for matches now.
        </Typography>
      )}
      {addResult && !addResult.ok && (
        <Typography variant="caption" style={{ display: "block", marginTop: 6, color: theme.palette.semantic.negative }}>
          Couldn't save that right now - try again shortly.
        </Typography>
      )}

      <Box display="flex" alignItems="center" style={{ margin: "18px 0" }}>
        <Divider style={{ flex: 1 }} />
        <Typography variant="caption" color="textSecondary" style={{ margin: "0 12px", fontWeight: 600 }}>
          OR
        </Typography>
        <Divider style={{ flex: 1 }} />
      </Box>

      {loading ? (
        <Box display="flex" alignItems="center" style={{ gap: "10px", padding: "16px 4px 4px" }}>
          <CircularProgress size={18} />
          <Typography variant="caption" color="textSecondary">
            Looking for addresses that show up more than once&hellip;
          </Typography>
        </Box>
      ) : hasGroups ? (
        <>
          <Box sx={{ marginTop: "16px" }}>
            {groups.map((group, i) => (
              <AddressRow
                key={group.address}
                group={group}
                onMine={onMine}
                onNotMine={onNotMine}
                onReview={onReview}
                isLast={i === groups.length - 1}
                inkColor={inkColor}
              />
            ))}
          </Box>

          <Pagination pageNumber={pageNumber} totalPages={totalPages} onPageChange={onPageChange} />
        </>
      ) : (
        <Typography variant="body2" color="textSecondary" style={{ marginTop: 16 }}>
          Nothing repeats often enough yet to suggest automatically.
        </Typography>
      )}
    </Box>
  );
};

export default AddressGroupsPanel;
